import React, {
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';
import styles from './styles.module.scss';
import { CloseButton } from './CloseButton';
import { ModalFooter } from './ModalFooter';
import { ModalHeader } from './ModalHeader';

interface IModalProps {
  id: string;
  children: ReactNode;
  onClose: () => void;
  isOpen: boolean;
  hideCloseButton?: boolean;
  styleOverride?: IStyleOverride;
}

export interface IStyleOverride {
  modal?: CSSProperties;
  container?: CSSProperties;
}

export const Modal = ({
  id,
  children,
  onClose,
  isOpen,
  hideCloseButton = false,
  styleOverride = {},
}: IModalProps) => {
  let footer = null;
  let header = null;

  const domElement = document.getElementById('root');
  const modalRef = useRef<HTMLDivElement>(null);

  const bodyOverflowRef = useRef<string | null>(null);
  const bodyPaddingRightRef = useRef<string | null>(null);

  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose]
  );

  // Scroll lock effect
  useEffect(() => {
    if (isOpen) {
      const { body } = document;

      // Save current values
      bodyOverflowRef.current = body.style.overflow;
      bodyPaddingRightRef.current = body.style.paddingRight;

      // Calculate scrollbar width before hiding overflow
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // Set overflow hidden and padding to prevent layout shift
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }

      window.addEventListener('keydown', handleEsc);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      if (isOpen) {
        const { body } = document;

        // Restore original values
        if (bodyOverflowRef.current !== null) {
          body.style.overflow = bodyOverflowRef.current;
        } else {
          body.style.overflow = '';
        }

        if (bodyPaddingRightRef.current !== null) {
          body.style.paddingRight = bodyPaddingRightRef.current;
        } else {
          body.style.paddingRight = '';
        }

        window.removeEventListener('keydown', handleEsc);
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [isOpen, handleEsc, handleClickOutside]);

  const content = React.Children.toArray(children).filter((child) => {
    if (React.isValidElement(child) && child.type === ModalFooter) {
      footer = child;
      return false;
    }
    if (React.isValidElement(child) && child.type === ModalHeader) {
      header = child;
      return false;
    }
    return true;
  });

  return (
    domElement &&
    isOpen &&
    createPortal(
      <FocusOn
        shards={[modalRef]}
        onClickOutside={onClose}
        onEscapeKey={onClose}
        scrollLock={false}
      >
        <div
          className={styles.modal}
          style={styleOverride?.modal}
          aria-modal="true"
          role="dialog"
          aria-label="Modal Dialog"
        >
          <div
            id={id}
            className={styles.modalContainer}
            style={styleOverride?.container}
            ref={modalRef}
          >
            <div className={styles.modalContent}>
              {header}
              {content}
            </div>
            {footer}
            {!hideCloseButton && <CloseButton onClose={onClose} />}
          </div>
        </div>
      </FocusOn>,
      domElement
    )
  );
};
